import { supabase } from "./supabase";

export interface PodcastEpisode {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  publishedAt: Date;
  duration: string;
  audioUrl: string;
  imageUrl: string;
  listen_count: number;
  like_count: number;
}

export interface Podcast {
  id: string;
  title: string;
  description: string;
  author: string;
  imageUrl: string;
  feedUrl: string;
  episodes: PodcastEpisode[];
  subscriber_count: number;
}

const corsProxy = "https://api.tempolabs.ai/proxy-asset?url=";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const retryWithDelay = async <T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY,
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retryWithDelay(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

const parseXML = async (xmlText: string) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "text/xml");
  console.log("Parsing XML feed:", xmlDoc);

  // Helper function to get text content safely
  const getElementText = (element: Element | null) =>
    element?.textContent?.trim() || "";

  // Parse channel info
  const channel = xmlDoc.querySelector("channel");
  if (!channel) throw new Error("No channel found in feed");

  const feed = {
    title: getElementText(channel.querySelector("title")),
    description: getElementText(channel.querySelector("description")),
    author:
      getElementText(channel.querySelector("itunes\\:author")) ||
      getElementText(channel.querySelector("author")),
    image:
      channel.querySelector("itunes\\:image")?.getAttribute("href") ||
      channel.querySelector("image > url")?.textContent ||
      "",
    items: Array.from(xmlDoc.querySelectorAll("item")).map((item) => ({
      guid: getElementText(item.querySelector("guid")),
      title: getElementText(item.querySelector("title")),
      description: getElementText(item.querySelector("description")),
      content: getElementText(item.querySelector("content\\:encoded")),
      pubDate: getElementText(item.querySelector("pubDate")),
      duration: getElementText(item.querySelector("itunes\\:duration")),
      image: item.querySelector("itunes\\:image")?.getAttribute("href") || "",
      enclosure: {
        url: item.querySelector("enclosure")?.getAttribute("url") || "",
      },
    })),
  };

  console.log("Parsed feed:", feed);
  return feed;
};

export const podcastService = {
  async addPodcast(feedUrl: string): Promise<Podcast> {
    try {
      console.log("Adding podcast with feed URL:", feedUrl);

      // First, check if podcast already exists
      const { data: existingPodcast } = await supabase
        .from("podcasts")
        .select("*")
        .eq("feed_url", feedUrl)
        .single();

      if (existingPodcast) {
        console.log("Podcast already exists, refreshing:", existingPodcast);
        return this.refreshPodcast(existingPodcast.id);
      }

      // Fetch and parse the RSS feed with retry logic
      console.log("Fetching RSS feed...");
      const response = await retryWithDelay(() =>
        fetch(corsProxy + encodeURIComponent(feedUrl)),
      );

      if (!response.ok)
        throw new Error(`Failed to fetch feed: ${response.statusText}`);

      const xmlText = await response.text();
      const feed = await parseXML(xmlText);

      // Create the podcast record
      console.log("Creating podcast record...");
      const { data: podcast, error: podcastError } = await supabase
        .from("podcasts")
        .insert({
          feed_url: feedUrl,
          title: feed.title || "Untitled Podcast",
          description: feed.description || "",
          author: feed.author || "",
          image_url: feed.image,
        })
        .select()
        .single();

      if (podcastError) throw podcastError;
      console.log("Created podcast:", podcast);

      // Process episodes
      console.log("Processing episodes...");
      const episodes = feed.items.map((item) => ({
        podcast_id: podcast.id,
        guid: item.guid || item.title, // Fallback to title if no guid
        title: item.title,
        description: item.content || item.description,
        published_at: new Date(item.pubDate).toISOString(),
        duration: item.duration || "",
        audio_url: item.enclosure.url || "",
        image_url: item.image || feed.image,
      }));

      // Insert episodes in batches to avoid timeout
      console.log("Inserting episodes...");
      for (let i = 0; i < episodes.length; i += 10) {
        const batch = episodes.slice(i, i + 10);
        const { error: episodesError } = await supabase
          .from("episodes")
          .insert(batch);

        if (episodesError) throw episodesError;
      }

      return this.getPodcast(podcast.id);
    } catch (error) {
      console.error("Error adding podcast:", error);
      throw error;
    }
  },

  async refreshPodcast(podcastId: string): Promise<Podcast> {
    console.log("Refreshing podcast:", podcastId);
    const { data: podcast, error: podcastError } = await supabase
      .from("podcasts")
      .select("*")
      .eq("id", podcastId)
      .single();

    if (podcastError) throw podcastError;

    // Fetch and parse the RSS feed
    console.log("Fetching RSS feed...");
    const response = await retryWithDelay(() =>
      fetch(corsProxy + encodeURIComponent(podcast.feed_url)),
    );

    if (!response.ok)
      throw new Error(`Failed to fetch feed: ${response.statusText}`);

    const xmlText = await response.text();
    const feed = await parseXML(xmlText);

    // Update podcast info
    console.log("Updating podcast info...");
    await supabase
      .from("podcasts")
      .update({
        title: feed.title || podcast.title,
        description: feed.description || podcast.description,
        author: feed.author || podcast.author,
        image_url: feed.image || podcast.image_url,
        updated_at: new Date().toISOString(),
      })
      .eq("id", podcastId);

    // Process new episodes
    console.log("Processing episodes...");
    const episodes = feed.items.map((item) => ({
      podcast_id: podcastId,
      guid: item.guid || item.title,
      title: item.title,
      description: item.content || item.description,
      published_at: new Date(item.pubDate).toISOString(),
      duration: item.duration || "",
      audio_url: item.enclosure.url || "",
      image_url: item.image || feed.image,
    }));

    // Upsert episodes in batches
    console.log("Upserting episodes...");
    for (let i = 0; i < episodes.length; i += 10) {
      const batch = episodes.slice(i, i + 10);
      await supabase
        .from("episodes")
        .upsert(batch, { onConflict: "podcast_id,guid" });
    }

    return this.getPodcast(podcastId);
  },

  async getPodcast(id: string): Promise<Podcast | null> {
    try {
      // First, get the podcast data
      const { data: podcast, error: podcastError } = await supabase
        .from("podcasts")
        .select("*, subscriber_count")
        .eq("id", id)
        .single();

      if (podcastError) {
        console.error("Error fetching podcast:", podcastError);
        return null;
      }

      if (!podcast) {
        console.error("No podcast found with id:", id);
        return null;
      }

      // Then, get the episodes
      const { data: episodes, error: episodesError } = await supabase
        .from("episodes")
        .select(
          `
          id,
          title,
          description,
          published_at,
          duration,
          audio_url,
          image_url
        `,
        )
        .eq("podcast_id", id);

      if (episodesError) {
        console.error("Error fetching episodes:", episodesError);
        return null;
      }

      // Get episode metrics in a separate query
      const { data: metrics, error: metricsError } = await supabase
        .from("episode_metrics")
        .select("episode_id, listen_count, like_count")
        .eq("podcast_id", id);

      if (metricsError) {
        console.error("Error fetching episode metrics:", metricsError);
        // Continue without metrics
      }

      // Create a map of episode metrics for easy lookup
      const metricsMap = (metrics || []).reduce(
        (acc, metric) => {
          acc[metric.episode_id] = metric;
          return acc;
        },
        {} as Record<string, any>,
      );

      // Transform the data to match our interface
      return {
        id: podcast.id,
        title: podcast.title,
        description: podcast.description,
        author: podcast.author,
        imageUrl: podcast.image_url,
        feedUrl: podcast.feed_url,
        subscriber_count: podcast.subscriber_count || 0,
        episodes: (episodes || []).map((episode) => ({
          id: episode.id,
          title: episode.title,
          description: episode.description || "",
          publishedAt: new Date(episode.published_at),
          duration: episode.duration || "",
          audioUrl: episode.audio_url,
          imageUrl: episode.image_url || podcast.image_url,
          listen_count: metricsMap[episode.id]?.listen_count || 0,
          like_count: metricsMap[episode.id]?.like_count || 0,
        })),
      };
    } catch (error) {
      console.error("Error in getPodcast:", error);
      return null;
    }
  },

  async getUserSubscriptions(userId: string): Promise<Podcast[]> {
    console.log("Getting subscriptions for user:", userId);
    const { data: subscriptions, error: subscriptionsError } = await supabase
      .from("user_subscriptions")
      .select("podcast_id")
      .eq("user_id", userId);

    if (subscriptionsError) {
      console.error("Error getting subscriptions:", subscriptionsError);
      throw subscriptionsError;
    }

    console.log("Found subscriptions:", subscriptions);

    const podcasts = await Promise.all(
      subscriptions.map((sub) => this.getPodcast(sub.podcast_id)),
    );

    console.log("Loaded podcasts:", podcasts);

    return podcasts;
  },

  async subscribeToPodcast(userId: string, podcastId: string): Promise<void> {
    console.log("Subscribing user to podcast:", { userId, podcastId });
    const { error } = await supabase
      .from("user_subscriptions")
      .insert({ user_id: userId, podcast_id: podcastId });

    if (error) throw error;
  },

  async unsubscribeFromPodcast(
    userId: string,
    podcastId: string,
  ): Promise<void> {
    console.log("Unsubscribing user from podcast:", { userId, podcastId });
    const { error } = await supabase
      .from("user_subscriptions")
      .delete()
      .eq("user_id", userId)
      .eq("podcast_id", podcastId);

    if (error) throw error;
  },

  async createBookmark(userId: string, episodeId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("bookmarks")
        .insert([{ user_id: userId, episode_id: episodeId }])
        .single();

      if (error) {
        // Handle specific foreign key violation errors
        if (error.code === "23503") {
          // Foreign key violation code
          console.error("Invalid user_id or episode_id provided");
          return false;
        }
        // Handle unique constraint violations
        if (error.code === "23505") {
          // Unique violation code
          console.error("Bookmark already exists");
          return false;
        }
        console.error("Error creating bookmark:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in createBookmark:", error);
      return false;
    }
  },

  async togglePodcastSubscription(
    userId: string,
    podcastId: string,
  ): Promise<{ success: boolean; subscribed: boolean }> {
    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from("user_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .eq("podcast_id", podcastId)
        .maybeSingle();

      const isSubscribed = !!existing;

      if (isSubscribed) {
        // Unsubscribe
        const { error: unsubError } = await supabase
          .from("user_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("podcast_id", podcastId);

        if (unsubError) throw unsubError;

        // Decrement subscriber count
        const { error: updateError } = await supabase.rpc(
          "decrement_subscriber_count",
          {
            podcast_id: podcastId,
          },
        );

        if (updateError) throw updateError;
      } else {
        // Subscribe
        const { error: subError } = await supabase
          .from("user_subscriptions")
          .insert([{ user_id: userId, podcast_id: podcastId }]);

        if (subError) throw subError;

        // Increment subscriber count
        const { error: updateError } = await supabase.rpc(
          "increment_subscriber_count",
          {
            podcast_id: podcastId,
          },
        );

        if (updateError) throw updateError;
      }

      return {
        success: true,
        subscribed: !isSubscribed,
      };
    } catch (error) {
      console.error("Error toggling subscription:", error);
      return {
        success: false,
        subscribed: false,
      };
    }
  },

  async getSubscriberCount(podcastId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from("podcasts")
        .select("subscriber_count")
        .eq("id", podcastId)
        .single();

      if (error) throw error;

      return data?.subscriber_count || 0;
    } catch (error) {
      console.error("Error getting subscriber count:", error);
      return 0;
    }
  },

  async getSubscriptionStatus(
    userId: string,
    podcastId: string,
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("user_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .eq("podcast_id", podcastId)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  },

  async updateListenProgress(
    userId: string,
    episodeId: string,
    podcastId: string,
    progress: number,
    completed: boolean,
  ): Promise<boolean> {
    try {
      const { data: existingProgress, error: fetchError } = await supabase
        .from("listen_history")
        .select()
        .eq("user_id", userId)
        .eq("episode_id", episodeId)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        console.error("Error fetching listen progress:", fetchError);
        return false;
      }

      if (existingProgress) {
        const { error: updateError } = await supabase
          .from("listen_history")
          .update({ progress, completed, updated_at: new Date().toISOString() })
          .eq("id", existingProgress.id);

        if (updateError) {
          console.error("Error updating listen progress:", updateError);
          return false;
        }
      } else {
        const { error: insertError } = await supabase
          .from("listen_history")
          .insert([
            {
              user_id: userId,
              episode_id: episodeId,
              podcast_id: podcastId,
              progress,
              completed,
            },
          ]);

        if (insertError) {
          console.error("Error creating listen progress:", insertError);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error in updateListenProgress:", error);
      return false;
    }
  },

  async getListenProgress(
    userId: string,
    episodeId: string,
  ): Promise<{ progress: number; completed: boolean } | null> {
    try {
      const { data, error } = await supabase
        .from("listen_history")
        .select("progress, completed")
        .eq("user_id", userId)
        .eq("episode_id", episodeId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null;
        }
        console.error("Error fetching listen progress:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error in getListenProgress:", error);
      return null;
    }
  },

  async getEpisodeInteractions(
    userId: string,
    episodeId: string,
  ): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from("liked_history")
        .select("interaction_type")
        .eq("user_id", userId)
        .eq("episode_id", episodeId);

      if (error) {
        console.error("Error fetching episode interactions:", error);
        return [];
      }

      return data.map((interaction) => interaction.interaction_type);
    } catch (error) {
      console.error("Error in getEpisodeInteractions:", error);
      return [];
    }
  },
};
